import {
  GraphQLArgument,
  GraphQLField,
  GraphQLInterfaceType,
  GraphQLObjectType,
  GraphQLSchema,
} from 'graphql';
import _ from 'lodash';

import { MatchSegments, explainRegexMatch } from 'src/lib/explainRegexMatch';

import { fuzzySearchRegexFactory } from './fuzzySearchRegexFactory';

export type FieldWithParent = {
  parentMatch: {
    type: GraphQLObjectType | GraphQLInterfaceType;
    matchedSegments: MatchSegments | null;
  };
  fieldMatch: {
    field: GraphQLField<unknown, unknown>;
    matchedSegments: MatchSegments | null;
  };
  argumentMatch?: {
    argument: GraphQLArgument;
    matchedSegments: MatchSegments;
  };
};

export const searchSchemaForFields = (
  graphqlSchema: GraphQLSchema,
  searchQuery: string,
) => {
  const typeMap = _.omitBy(graphqlSchema.getTypeMap(), (_value, key) =>
    key.startsWith('__'),
  );
  const allTypes = Object.values(typeMap);

  const [, parentTypeQuery, fieldNameQuery] =
    searchQuery.match(/^(\w*)\.(.*)/) || [];

  const shouldNameSpaceByTypeName = !!parentTypeQuery;

  const typeNameRegex = fuzzySearchRegexFactory(parentTypeQuery || searchQuery);
  const fieldNameRegex = fuzzySearchRegexFactory(fieldNameQuery || searchQuery);
  const argNameRegex = fuzzySearchRegexFactory(searchQuery);

  return allTypes.flatMap((type) => {
    if (
      !(
        type instanceof GraphQLObjectType ||
        type instanceof GraphQLInterfaceType
      )
    ) {
      return [];
    }
    const fieldMap = type.getFields();
    const typeMatch = typeNameRegex.exec(type.name);

    if (shouldNameSpaceByTypeName && !typeMatch) {
      return [];
    }

    return Object.values(fieldMap).flatMap((field): FieldWithParent[] => {
      const fieldMatch = fieldNameRegex.exec(field.name);
      const fieldResults: FieldWithParent[] =
        (!fieldMatch && !typeMatch) ||
        (shouldNameSpaceByTypeName && fieldNameQuery && !fieldMatch)
          ? []
          : [
              {
                parentMatch: {
                  type,
                  matchedSegments: explainRegexMatch(typeMatch),
                },
                fieldMatch: {
                  field,
                  matchedSegments: explainRegexMatch(fieldMatch),
                },
              },
            ];
      const argResults: FieldWithParent[] = shouldNameSpaceByTypeName
        ? []
        : field.args.flatMap((argument) => {
            const argMatch = argNameRegex.exec(argument.name);
            if (!argMatch) {
              return [];
            }
            return [
              {
                parentMatch: {
                  type,
                  matchedSegments: explainRegexMatch(typeMatch),
                },
                fieldMatch: {
                  field,
                  matchedSegments: null,
                },
                argumentMatch: {
                  argument,
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  matchedSegments: explainRegexMatch(argMatch)!,
                },
              },
            ];
          });
      return [...fieldResults, ...argResults];
    });
  });
};

export const fieldSearchKindSorters = [
  // prioritize results where the field name matches
  (result: FieldWithParent) => (result.fieldMatch.matchedSegments ? 0 : 1),
];

export const fieldSearchLengthSorters = [
  // then prioritize results with longer consecutive matches on fieldName
  (result: FieldWithParent) =>
    -(
      _.maxBy(
        result.fieldMatch.matchedSegments?.filter((segment) => segment.matched),
        (segment) => segment.text.length,
      )?.text.length || 0
    ),

  // then prioritize by occurrence index of first match
  (result: FieldWithParent) => {
    const firstMatch = result.fieldMatch.matchedSegments?.find(
      (segment) => segment.matched,
    );
    return firstMatch
      ? result.fieldMatch.field.name.indexOf(firstMatch.text)
      : Infinity;
  },

  // then prioritize by fewest number of unmatched characters
  (result: FieldWithParent) =>
    _.sumBy(
      result.fieldMatch.matchedSegments?.filter((segment) => !segment.matched),
      (segment) => segment.text.length,
    ),

  // then prioritize results with longer consecutive matches on parentName
  (result: FieldWithParent) =>
    -(
      _.maxBy(
        result.parentMatch.matchedSegments?.filter(
          (segment) => segment.matched,
        ),
        (segment) => segment.text.length,
      )?.text.length || 0
    ),
];
